import { useCallback } from 'react';

const STORAGE_KEY = 'akashic_data_pipeline_store';

export type AdpNodeRecord = Record<string, unknown>;
export type AdpStore = Record<string, AdpNodeRecord>;

const isProd = import.meta.env.PROD;

// Static key
const SECRET = 'akashic-pipeline-key';

/**
 * Simple XOR-based obfuscation
 */
function xorCipher(input: string, key: string): string {
    let output = '';

    for (let i = 0; i < input.length; i++) {
        const codePoint = (input.codePointAt(i) ?? 0) ^ (key.codePointAt(i % key.length) ?? 0);
        output += String.fromCodePoint(codePoint);
    }

    return output;
}

/**
 * Encode → obfuscate + base64
 */
function encode(data: string): string {
    const xored = xorCipher(data, SECRET);
    const bytes = new TextEncoder().encode(xored);
    const binary = Array.from(bytes, (b) => String.fromCodePoint(b)).join('');
    return btoa(binary);
}

/**
 * Decode → reverse
 */
function decode(encoded: string): string | null {
    try {
        const binary = atob(encoded);
        const bytes = Uint8Array.from(binary, (c) => c.codePointAt(0));
        const decoded = new TextDecoder().decode(bytes);
        return xorCipher(decoded, SECRET);
    } catch {
        return null;
    }
}

/**
 * Read store (sync)
 */
function readStore(): AdpStore {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    try {
        if (!isProd) {
            return JSON.parse(raw) as AdpStore;
        }

        const decoded = decode(raw);
        return decoded ? (JSON.parse(decoded) as AdpStore) : {};
    } catch {
        return {};
    }
}

/**
 * Write store (sync)
 */
function writeStore(store: AdpStore): void {
    const serialized = JSON.stringify(store);

    if (!isProd) {
        sessionStorage.setItem(STORAGE_KEY, serialized);
        return;
    }

    const encoded = encode(serialized);
    sessionStorage.setItem(STORAGE_KEY, encoded);
}

export function getAdpSessionNode(nodeId: string): AdpNodeRecord | null {
    const store = readStore();
    return store[nodeId] ?? null;
}

export function mergeAdpSessionNode(nodeId: string, data: AdpNodeRecord): void {
    const store = readStore();

    store[nodeId] = {
        ...store[nodeId],
        ...data,
    };

    writeStore(store);
}

/**
 * Hook
 */
export function useAdpSessionStore() {
    const getAll = useCallback((): AdpStore => {
        return readStore();
    }, []);

    const getNode = useCallback((nodeId: string): AdpNodeRecord | null => {
        return getAdpSessionNode(nodeId);
    }, []);

    const setNode = useCallback((nodeId: string, data: AdpNodeRecord): void => {
        mergeAdpSessionNode(nodeId, data);
    }, []);

    const replaceNode = useCallback((nodeId: string, data: AdpNodeRecord): void => {
        const store = readStore();
        store[nodeId] = data;
        writeStore(store);
    }, []);

    const removeNode = useCallback((nodeId: string): void => {
        const store = readStore();
        delete store[nodeId];
        writeStore(store);
    }, []);

    const clearAll = useCallback((): void => {
        sessionStorage.removeItem(STORAGE_KEY);
    }, []);

    return {
        getAll,
        getNode,
        setNode,
        replaceNode,
        removeNode,
        clearAll,
    };
}